import { Controller } from '@hotwired/stimulus'
import posthog from 'posthog-js'
import { beforeSend } from 'helpers/posthog/scrub'

const API_HOST = 'https://us.i.posthog.com'

// Mounted once per layout by ApplicationHelper#render_posthog, whose element also binds the actions:
// `posthog:capture@window` from posthog_event_controller.js emitters, and `turbo:load@document` /
// `turbo:before-visit@document` / `turbo:reload@document` because Turbo Drive swaps the body without reloading, so the
// SDK's init-only $pageview would never re-fire on navigation. There is no `window.posthog` global.
export default class PosthogController extends Controller {
  // The landing $pageview is captured by the first connect in a JS context, not by Turbo's initial-load turbo:load:
  // that one only fires at window load, by which time a slow page's user may already have visited elsewhere (GH#32693).
  static landed = false

  static values = {
    apiKey: String,
    eventsEnabled: Boolean,
    userId: String,
    email: String,
    personId: { type: String, default: 'none' },
    impersonatorId: { type: String, default: 'none' },
    impersonatorEmail: { type: String, default: 'none' }
  }

  connect() {
    if (!posthog.__loaded) posthog.init(this.apiKeyValue, this.config)
    this.identify()
    this.captureLanding()
  }

  capture({ detail: { name, properties } }) {
    this.captureIfEnabled(name, properties)
  }

  pageview({ detail }) {
    // No visitStart means the initial-load dispatch, already counted by connect. After turbo:reload the visit was
    // invalidated: Turbo still dispatches turbo:load, with the URL of the page being left.
    if (this.reloading || !detail.timing.visitStart) return

    this.captureIfEnabled('$pageview')
  }

  pageleave() {
    this.captureIfEnabled('$pageleave')
  }

  reload() {
    this.reloading = true
  }

  identify() {
    if (!this.userIdValue) {
      posthog.reset()
      return
    }

    posthog.identify(this.userIdValue, { email: this.emailValue })
    posthog.register({
      impersonator_email: this.impersonatorEmailValue,
      impersonator_id: this.impersonatorIdValue,
      person_id: this.personIdValue
    })
  }

  captureLanding() {
    if (PosthogController.landed) return

    PosthogController.landed = true
    this.captureIfEnabled('$pageview')
  }

  captureIfEnabled(...args) {
    if (!this.eventsEnabledValue) return

    posthog.capture(...args)
  }

  get config() {
    return {
      api_host: API_HOST,
      person_profiles: 'identified_only',
      autocapture: this.eventsEnabledValue,
      capture_pageview: false,
      capture_pageleave: this.eventsEnabledValue,
      before_send: beforeSend,
      session_recording: {
        maskTextSelector: '.ph-masked',
        maskInputFn: (text, element) => {
          if (element?.attributes['data-ph-masked']?.value === 'true') {
            return '*'.repeat(text.length)
          }
        }
      }
    }
  }
}
